import React from 'react';
import {
  DocumentIcon,
  MusicalNoteIcon,
  FilmIcon,
  CodeBracketIcon,
  DocumentTextIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/solid';

interface FileTypeIconProps {
  mime?: string;
  filename?: string;
  className?: string;
}

interface IconSpec {
  Icon: React.ComponentType<{ className?: string }>;
  bg: string;
  fg: string;
}

const SPECS: Array<{ test: (mime: string, name: string) => boolean; spec: IconSpec }> = [
  {
    test: (m) => m.startsWith('audio/'),
    spec: {
      Icon: MusicalNoteIcon,
      bg: 'bg-orange-100 dark:bg-orange-900/30',
      fg: 'text-orange-600 dark:text-orange-300',
    },
  },
  {
    test: (m) => m.startsWith('video/'),
    spec: {
      Icon: FilmIcon,
      bg: 'bg-rose-100 dark:bg-rose-900/30',
      fg: 'text-rose-600 dark:text-rose-300',
    },
  },
  {
    test: (m, n) => m === 'application/pdf' || n.endsWith('.pdf'),
    spec: {
      Icon: DocumentTextIcon,
      bg: 'bg-amber-100 dark:bg-amber-900/30',
      fg: 'text-amber-600 dark:text-amber-300',
    },
  },
  {
    test: (m, n) =>
      m.includes('zip') || m.includes('rar') || /\.(zip|rar|7z|tar|gz)$/i.test(n),
    spec: {
      Icon: ArchiveBoxIcon,
      bg: 'bg-violet-100 dark:bg-violet-900/30',
      fg: 'text-violet-600 dark:text-violet-300',
    },
  },
  {
    test: (m, n) =>
      m.includes('javascript') ||
      m.includes('json') ||
      m.includes('html') ||
      /\.(js|jsx|ts|tsx|json|html|css|py|java|cpp|go|rs|rb)$/i.test(n),
    spec: {
      Icon: CodeBracketIcon,
      bg: 'bg-success-100 dark:bg-success-900/30',
      fg: 'text-success-600 dark:text-success-300',
    },
  },
  {
    test: (m, n) => m === 'text/plain' || /\.(txt|md|log)$/i.test(n),
    spec: {
      Icon: DocumentTextIcon,
      bg: 'bg-neutral-200 dark:bg-neutral-700',
      fg: 'text-neutral-600 dark:text-neutral-300',
    },
  },
];

const DEFAULT_SPEC: IconSpec = {
  Icon: DocumentIcon,
  bg: 'bg-neutral-200 dark:bg-neutral-700',
  fg: 'text-neutral-500 dark:text-neutral-400',
};

const FileTypeIcon: React.FC<FileTypeIconProps> = ({ mime, filename, className }) => {
  const m = (mime ?? '').toLowerCase();
  const n = (filename ?? '').toLowerCase();
  const spec = SPECS.find(({ test }) => test(m, n))?.spec ?? DEFAULT_SPEC;
  const { Icon, bg, fg } = spec;
  return (
    <div
      className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${bg} ${className ?? ''}`}
    >
      <Icon className={`w-5 h-5 ${fg}`} />
    </div>
  );
};

export default FileTypeIcon;
